import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { toString } from "lodash";
import { useSearchParams } from "react-router";

import {
  extractGasInfo,
  hasGasInfo,
  Network,
  NetworkToGasToken,
} from "@/lib/chains";
import { LGT_SERVER_URL } from "@/lib/env";
import useStore from "@/state";

import {
  downloadAuditLogs,
  getCustomVerificationsCount,
  getCustomVerificationTypes,
  getImages,
  getOrganization,
  getOrganizationInvitations,
  getOrganizationMemberships,
  getOrganizations,
  getSKU,
  getSKUAnalytics,
  getSKUs,
  getSKUsForSKUSelection,
  getTag,
  getTagAnalytics,
  getTags,
  getTagVerifications,
  getTracks,
  getVideos,
  getWidgetToken,
} from "./api";
import { GasPrice, TokenPrice, WorkOSPermission, WorkOSSession } from "./types";

type ListQueryArgs = {
  defaultSort?: { column: string; direction: "asc" | "desc" };
};

export const useGetSKUsQuery = ({ defaultSort }: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const search = searchParams.get("search");

  return useQuery({
    queryKey: ["skus", search, page, limit, sort, direction],
    queryFn: () => {
      return getSKUs({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetSKUsForSKUSelectionQuery = () => {
  const [searchParams] = useSearchParams();

  const search = searchParams.get("search");

  return useQuery({
    queryKey: ["skus", "list", search],
    queryFn: () => {
      return getSKUsForSKUSelection({
        search,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetSKUQuery = (id: string) => {
  return useQuery({
    queryKey: ["skus", id],
    queryFn: () => getSKU(id),
  });
};

export const useGetSKUAnalyticsQuery = (id: string, from: Date, to: Date) => {
  const start = formatISO(from);
  const end = formatISO(to);

  return useQuery({
    queryKey: ["skus", id, "analytics", start, end],
    queryFn: () => getSKUAnalytics(id, start, end),
  });
};

export const useGetTagsQuery = ({ defaultSort }: ListQueryArgs = {}) => {
  const [searchParams] = useSearchParams();

  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const sort = searchParams.get("sort") || defaultSort?.column;
  const direction = searchParams.get("direction") || defaultSort?.direction;

  const templateId = searchParams.get("templateId");
  const uid = searchParams.get("uid");

  return useQuery({
    queryKey: ["tags", templateId, uid, page, limit, sort, direction],
    queryFn: () => {
      return getTags({
        template_id: templateId,
        uid,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetTagQuery = (id: string) => {
  return useQuery({
    queryKey: ["tags", id],
    queryFn: () => getTag(id),
  });
};

export const useGetSKUForTagQuery = (
  tagUid?: string,
  skuId?: number | null,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["tags", tagUid, "skus", skuId],
    queryFn: () => getSKU(toString(skuId)),
    enabled,
  });
};

export const useGetTagAnalyticsQuery = (uid: string, from: Date, to: Date) => {
  const start = formatISO(from);
  const end = formatISO(to);

  return useQuery({
    queryKey: ["skus", uid, "analytics", start, end],
    queryFn: () => getTagAnalytics(uid, start, end),
  });
};

export const useGetVerificationsQuery = ({ defaultSort }: ListQueryArgs) => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort") ?? defaultSort?.column;
  const direction = searchParams.get("direction") ?? defaultSort?.direction;

  const templateId = searchParams.get("templateId");
  const uid = searchParams.get("uid");
  const verifyType = searchParams.get("verifyType");
  const customVerificationTypeId = searchParams.get("customVerificationTypeId");

  return useQuery({
    queryKey: [
      "verifications",
      templateId,
      uid,
      verifyType,
      page,
      limit,
      sort,
      direction,
    ],
    queryFn: () => {
      return getTagVerifications({
        template_id: templateId,
        uid,
        verify_type: verifyType,
        custom_verification_type_id: customVerificationTypeId,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetOrganizationQuery = () => {
  return useQuery({
    queryKey: ["organization"],
    queryFn: () => {
      return getOrganization();
    },
  });
};

export const useAuthCallbackQuery = () => {
  const { setSession } = useStore();

  return useQuery({
    queryKey: ["auth-callback"],
    queryFn: async () => {
      const searchParams = new URLSearchParams(window.location.search);

      const response = await fetch(
        `${LGT_SERVER_URL}/auth/callback?${searchParams.toString()}`,
      );
      const data = await response.json();

      const token = data.token;

      if (token) {
        localStorage.setItem("token", token);
      } else {
        throw new Error("No token found in response");
      }

      const sessionResponse = await fetch(`${LGT_SERVER_URL}/auth/session`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const session: WorkOSSession = await sessionResponse.json();

      setSession(session);

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        user_id: session.user.id,
        user_organization_id: session.organization_id,
        user_email: session.user.email,
        user_role: session.role,
      });

      return data;
    },
  });
};

export const useDownloadAuditLogsQuery = (exportId?: string) => {
  return useQuery({
    queryKey: ["audit-logs", exportId],
    queryFn: () => {
      return downloadAuditLogs(exportId!);
    },
    enabled: !!exportId,
    refetchInterval: (query) =>
      query.state.data?.state === "ready" ? false : 1000,
  });
};

export const useGasPriceQuery = (
  chainId?: number,
  setValue?: (value: number) => void,
) => {
  return useQuery({
    queryKey: ["gas-price", chainId],
    queryFn: async () => {
      const response = await fetch(
        `https://gas-api.metaswap.codefi.network/networks/${chainId}/suggestedGasFees`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const gasPrice = (await response.json()) as GasPrice;
      const gasInfo = extractGasInfo(gasPrice, chainId!);
      setValue?.(Number(gasInfo.currentGas.toPrecision(4)));
      return gasInfo;
    },
    enabled: !!chainId && hasGasInfo(chainId),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
};

export const useGetOrganizationsQuery = () => {
  return useQuery({
    queryKey: ["organizations"],
    queryFn: () => {
      return getOrganizations();
    },
  });
};

export const useGetOrganizationMembershipsQuery = () => {
  return useQuery({
    queryKey: ["organization-memberships"],
    queryFn: () => {
      return getOrganizationMemberships();
    },
  });
};

export const useGetOrganizationInvitationssQuery = () => {
  return useQuery({
    queryKey: ["organization-invitations"],
    queryFn: () => {
      return getOrganizationInvitations();
    },
  });
};

export const useGetWidgetTokenQuery = (scopes?: WorkOSPermission[]) => {
  return useQuery({
    queryKey: ["widget-token", scopes],
    queryFn: () => {
      return getWidgetToken(scopes);
    },
  });
};

const COIN_GECKO_API = "https://api.coingecko.com/api/v3/coins";
const COIN_GECKO_DEMO_API_KEY = "CG-YCLG2CgyypNjvBy2W6HRLK4h";

export const useGetTokenPriceQuery = (
  chainId: Network,
  setValue: (value: number) => void,
) => {
  return useQuery({
    queryKey: ["token-price", chainId],
    queryFn: async () => {
      const response = await fetch(
        `${COIN_GECKO_API}/${NetworkToGasToken[chainId]}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-cg-demo-api-key": COIN_GECKO_DEMO_API_KEY,
          },
        },
      );

      if (!response.ok) {
        throw new Error(await response.json());
      }

      const data: TokenPrice = await response.json();
      setValue(data.market_data.current_price.usd);
      return data;
    },
    enabled: !!NetworkToGasToken[chainId],
  });
};

export const useGetImagesQuery = () => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort");
  const direction = searchParams.get("direction");

  const search = searchParams.get("search");

  return useQuery({
    queryKey: ["images", search, page, limit, sort, direction],
    queryFn: () => {
      return getImages({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetVideosQuery = () => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort");
  const direction = searchParams.get("direction");

  const search = searchParams.get("search");

  return useQuery({
    queryKey: ["videos", search, page, limit, sort, direction],
    queryFn: () => {
      return getVideos({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetTracksQuery = () => {
  const [searchParams] = useSearchParams();

  const limit = searchParams.get("limit");
  const page = searchParams.get("page");
  const sort = searchParams.get("sort");
  const direction = searchParams.get("direction");

  const search = searchParams.get("search");

  return useQuery({
    queryKey: ["tracks", search, page, limit, sort, direction],
    queryFn: () => {
      return getTracks({
        search,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        sort,
        direction,
      });
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetCustomVerificationTypesQuery = () => {
  return useQuery({
    queryKey: ["custom-verification-types"],
    queryFn: () => {
      return getCustomVerificationTypes();
    },
  });
};

export const useGetCustomVerificationsCountQuery = (tagUid: string) => {
  return useQuery({
    queryKey: ["custom-verification-types", "count", tagUid],
    queryFn: () => {
      return getCustomVerificationsCount(tagUid);
    },
  });
};
